<template>
  <b-modal ref="modal" :id="modalId" centered hide-footer title="Training Detail" size="xl" hide-header-close>
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading training detail...</p>
    </div>

    <div v-else-if="data">
      <!-- Course Content -->
      <div class="row g-0">
        <!-- Full Width - Course Info -->
        <div class="col-md-12">
          <!-- Header Section -->
          <div class="row mb-4">
            <div class="col-md-7 d-flex flex-column justify-content-between">
              <div class="d-flex flex-column">
                <!-- Status Badge -->
                <div class="mb-3">
                  <span :class="getTrainingStatusClass(data.status)">
                    {{ snakeToTitle(data.status) }}
                  </span>
                </div>

                <!-- Course Title -->
                <h3 class="mb-3 font-weight-bold" style="color: #1a202c">
                  {{ data.training?.title || "-" }}
                </h3>

                <!-- Category Badges -->
                <div class="mb-4 d-flex gap-2">
                  <span class="badge-icon bg-danger-soft text-danger">
                    <i :class="getTrainingTypeIcon(data.training?.type?.name)"></i>
                  </span>
                  <span class="badge-custom bg-warning-soft text-warning">
                    {{ (data.competency && data.competency.name) || "-" }}
                  </span>
                  <!-- <span class="badge-custom bg-info-soft text-info">
                    {{ (data.competency && data.competency[0]?.type) || "-" }}
                  </span> -->
                </div>
              </div>

              <!-- Stats -->
              <div class="row p-3 rounded" style="background-color: #fafafa">
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted gap-1">
                    <i class="fe fe-clock me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem">Learning Hours</small>
                      <strong class="text-dark">{{ data.training?.hour ? Number(data.training.hour) : "-" }} hrs</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted gap-1">
                    <i class="fe fe-tag me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem">Price</small>
                      <strong class="text-dark">{{ formatCurrency(data.training?.currency, data.training?.price) }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted gap-1">
                    <i class="fe fe-users me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem">Participants</small>
                      <strong class="text-dark">{{ data.training?.total_participants }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Course Image -->
            <div class="col-md-5">
              <img
                :src="data.training.cover_image_path || 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course'"
                alt="Course Thumbnail"
                class="w-100 rounded"
                style="height: 280px; object-fit: cover"
              />
            </div>
          </div>

          <!-- Tabs -->
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item">
              <a
                :class="isActiveTab('description') ? 'nav-link active' : 'nav-link'"
                href="#"
                @click.prevent="setActiveTab('description')"
              >
                Description
              </a>
            </li>
            <li class="nav-item">
              <a
                :class="isActiveTab('attributes') ? 'nav-link active' : 'nav-link'"
                href="#"
                @click.prevent="setActiveTab('attributes')"
              >
                Attributes
              </a>
            </li>
            <li class="nav-item">
              <a
                :class="isActiveTab('platform') ? 'nav-link active' : 'nav-link'"
                href="#"
                @click.prevent="setActiveTab('platform')"
              >
                Platform
              </a>
            </li>
            <li v-if="data.training.platform === 'Other'" class="nav-item">
              <a
                :class="isActiveTab('schedule') ? 'nav-link active' : 'nav-link'"
                href="#"
                @click.prevent="setActiveTab('schedule')"
              >
                Schedule
              </a>
            </li>
            <li v-if="data.training.platform === 'Ugems University'" class="nav-item">
              <a
                :class="isActiveTab('completion') ? 'nav-link active' : 'nav-link'"
                href="#"
                @click.prevent="setActiveTab('completion')"
              >
                Completion
              </a>
            </li>
            <li v-if="data?.certificate" class="nav-item">
              <a
                :class="isActiveTab('certificate') ? 'nav-link active' : 'nav-link'"
                href="#"
                @click.prevent="setActiveTab('certificate')"
              >
                Certificate
              </a>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Description Tab -->
            <div
              v-show="isActiveTab('description')"
              class="tab-pane fade show active"
              id="description"
            >
              <p class="mb-3" style="line-height: 1.8; font-size: 0.9rem">
                {{ data.training.description || "No description available." }}
              </p>

              <!-- Related Competencies -->
              <!-- <div class="mt-4" v-if="data.training?.competency_relations?.length > 0">
                <h6 class="font-weight-bold mb-3">Related Competencies</h6>
                <div class="d-flex gap-2 flex-wrap">
                  <span
                    v-for="(comp, index) in data.training?.competency_relations"
                    :key="index"
                    class="badge bg-white text-dark border px-3 py-2"
                    style="font-weight: 500"
                  >
                    {{ comp.competency ? comp.competency.name : "-" }} <span class="text-muted">|</span>
                    <i class="fe fe-star text-warning"></i>
                    {{ comp.proficiency ? comp.proficiency : "-" }}
                  </span>
                </div>
                <p class="text-muted mt-3 mb-0" style="font-size: 0.75rem">
                  **Notes: <i class="fe fe-star text-warning"></i> Levels of proficiency 1-5
                </p>
              </div> -->
            </div>

            <!-- TabAttributes -->
            <div v-show="isActiveTab('attributes')" class="tab-pane fade show active" id="attributes">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Course Method -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Method</label>
                    <p class="text-dark font-weight-bold mb-0">
                      {{ data.training?.method?.name || data.training?.events[0]?.method?.name || "-" }}
                    </p>
                  </div>

                  <!-- Course Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.category?.title || "-" }}</p>
                  </div>

                  <!-- Course URL -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course URL</label>
                    <div class="d-flex align-items-center gap-2">
                      <a
                        v-if="data.training.website"
                        :href="data.training.website"
                        target="_blank"
                        class="text-primary text-truncate"
                        style="max-width: 80%; text-decoration: none"
                      >
                        {{ data.training.website }}
                      </a>
                      <span v-else class="text-muted">-</span>
                      <a
                        v-if="data.training.website"
                        :href="data.training.website"
                        target="_blank"
                        class="btn btn-sm btn-danger d-flex align-items-center justify-content-center"
                        style="width: 32px; height: 32px; padding: 0; border-radius: 4px"
                      >
                        <i class="fe fe-send" style="font-size: 14px"></i>
                      </a>
                    </div>
                  </div>

                  <!-- Department -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Department</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training.department || "-" }}</p>
                  </div>

                  <!-- Division -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Division</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training.division || "-" }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Course Type -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Type</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.type?.name || "-" }}</p>
                  </div>

                  <!-- Certificate Validity Period -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Certificate Validity Period (Months)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.certificate_validity_period || "-" }}</p>
                  </div>

                  <!-- Course Scope -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Scope</label>
                    <div>
                      <span
                        v-for="scope in data.training?.training_scope"
                        :key="scope"
                        class="badge badge-dark font-weight-bold mr-1"
                      >
                        {{ scope }}
                      </span>
                    </div>
                  </div>

                  <!-- STEM Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">STEM Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training.stem_category || "-" }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Platform Tab -->
            <div v-show="isActiveTab('platform')" class="tab-pane fade show active" id="platform">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Platform Name -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Platform Name</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.platform || "-" }}</p>
                  </div>

                  <!-- Training Provider (LDP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Training Provider</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.name || "-" }}</p>
                  </div>

                  <!-- Provider Website -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Provider Website</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.website || "-" }}</p>
                  </div>

                  <!-- Email -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Email</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.email || "-" }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Provider Name (LSP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Provider Name (LSP)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.lsp?.name || "-" }}</p>
                  </div>

                  <!-- Address -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Address</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.address || "-" }}</p>
                  </div>

                  <!-- Contact Person -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Contact Person</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.contact_person || "-" }}</p>
                  </div>

                  <!-- Phone -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Phone</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.phone || "-" }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="data?.certificate" v-show="isActiveTab('certificate')" class="tab-pane fade show active" id="certificate">
              <!-- Certificate Section -->
              <div class="row">
                <div class="col-md-8">
                  <!-- Has Certificate -->
                  <div class="d-flex gap-3 flex-column">
                    <div
                      v-if="isPdfCertificate(data?.certificate)"
                      class="course-image-container rounded"
                      style="cursor: pointer"
                      @click="previewCertificate"
                    >
                      <div class="pdf-overlay">
                        <div class="pdf-icon-container-large">
                          <i class="fe fe-file-text"></i>
                        </div>
                        <p class="pdf-label mt-3 mb-1">PDF Certificate</p>
                        <small class="text-muted">Click to view</small>
                      </div>
                    </div>
                    <!-- Image Certificate - Same style as course image -->
                    <div v-else class="course-image-container rounded" style="cursor: pointer" @click="previewCertificate">
                      <img :src="data?.certificate" alt="Certificate" class="course-image" @error="handleCertImageError" />
                    </div>

                    <!-- Action Buttons -->
                    <!-- <div class="d-flex justify-content-center gap-2 mt-3">
                      <button v-if="data?.status === 'completed'" class="btn btn-success" @click="downloadCertificate">
                        <i class="fe fe-download me-1"></i>
                        Download
                      </button>
                    </div> -->
                  </div>
                </div>

                <!-- <div class="col-md-4">
                  <div class="info-section p-4 rounded border" style="background-color: #fafafa">
                    <h6 class="font-weight-bold mb-3"><i class="fe fe-info me-2"></i>Certificate Information</h6>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Certificate Validity Period</label>
                      <p class="text-dark font-weight-bold mb-0">{{ data?.certificate_validity_period || "-" }} Months</p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Completion Date</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ data?.completion_date ? formatLongDate(data?.completion_date) : "-" }}
                      </p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Training Method</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ data?.training_method_name || data?.training_method || "-" }}
                      </p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Training Type</label>
                      <p class="text-dark font-weight-bold mb-0">{{ formatType(data?.training_type) }}</p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Enrollment Status</label>
                      <span :class="getTrainingStatusClass(data?.status)">
                        {{ modifyStatus(data?.status) }}
                      </span>
                    </div>
                  </div>
                </div> -->
              </div>
            </div>

            <!-- Schedule Tab -->
            <div
              v-if="data.training.platform === 'Other'"
              v-show="isActiveTab('schedule')"
              class="tab-pane fade show active"
              id="schedule"
            >
              <!-- Schedule Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="rounded" style="background-color: #f8f9fa">
                    <tr>
                      <th class="text-muted small font-weight-normal">BATCH</th>
                      <th class="text-muted small font-weight-normal">PENYELENGGARA</th>
                      <th class="text-muted small font-weight-normal">QUOTA</th>
                      <th class="text-muted small font-weight-normal">USED QUOTA</th>
                      <th class="text-muted small font-weight-normal">REMAINING QUOTA</th>
                      <th class="text-muted small font-weight-normal">SCHEDULE</th>
                      <th class="text-muted small font-weight-normal">STATUS</th>
                      <th class="text-muted small font-weight-normal">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Empty State -->
                    <tr v-if="data?.training?.events?.length === 0">
                      <td colspan="8" class="text-center py-5">
                        <i class="fe fe-inbox text-muted" style="font-size: 3rem"></i>
                        <p class="text-muted mt-2">No batches available</p>
                      </td>
                    </tr>

                    <!-- Batch Rows -->
                    <tr v-for="(batch, index) in data?.training?.events" :key="batch.id || index">
                      <td class="align-middle">{{ formatBatchLabel(batch) }}</td>
                      <td class="align-middle">{{ formatPenyelenggara(batch.penyelenggara) }}</td>
                      <td class="align-middle">{{ batch.quota || "-" }}</td>
                      <td class="align-middle">{{ batch?.participant_count }}</td>
                      <td class="align-middle">{{ batch?.remaining_quota || "-" }}</td>
                      <td class="align-middle">
                        <div v-if="batch.schedules && batch.schedules.length > 0">
                          <div v-for="(schedule, idx) in batch.schedules" :key="idx" :class="{ 'mt-2': idx > 0 }">
                            <strong class="d-block">{{ formatLongDate(schedule.date) || "-" }}</strong>
                            <small class="text-muted"
                              >{{ formatTime(schedule.start_time) || "-" }} -
                              {{ formatTime(schedule.end_time) || "-" }}</small
                            >
                          </div>
                        </div>
                        <span v-else class="text-muted">No schedule</span>
                      </td>
                      <td class="align-middle">
                        <div v-if="batch.schedules && batch.schedules.length > 0">
                          <div v-for="(schedule, idx) in batch.schedules" :key="idx" :class="{ 'mt-4': idx > 0 }">
                            <strong class="d-block">{{ snakeToTitle(schedule.status) || "-" }}</strong>
                          </div>
                        </div>
                        <span v-else class="text-muted">{{ batch.status }}</span>
                      </td>
                      <td class="align-middle">
                        <div class="d-flex gap-2">
                          <b-button
                            v-if="batch.isAllowedSubmitParticipant && data.status === 'open'"
                            class="no-wrap btn-pill"
                            variant="warning"
                            size="sm"
                            @click="handleSubmit(batch)"
                          >
                            <span class="fe fe-edit"></span>
                            Submit Enrollment
                          </b-button>
                          <span v-else :class="getTrainingStatusClass(batch?.status)">
                            {{ modifyStatus(batch?.status) || "" }}
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Completion Tab -->
            <div
              v-if="data.training.platform === 'Ugems University'"
              v-show="isActiveTab('completion')"
              class="tab-pane fade show active"
              id="completion"
            >
              <!-- Schedule Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="rounded" style="background-color: #f8f9fa">
                    <tr>
                      <th class="text-muted small font-weight-normal">BATCH</th>
                      <th class="text-muted small font-weight-normal">PENYELENGGARA</th>
                      <th class="text-muted small font-weight-normal">STATUS</th>
                      <th class="text-muted small font-weight-normal">POST TEST</th>
                      <!-- <th class="text-muted small font-weight-normal">MOODLE</th> -->
                      <th class="text-muted small font-weight-normal">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Empty State -->
                    <tr v-if="data?.training?.events?.length === 0">
                      <td colspan="5" class="text-center py-5">
                        <i class="fe fe-inbox text-muted" style="font-size: 3rem"></i>
                        <p class="text-muted mt-2">No batches available</p>
                      </td>
                    </tr>

                    <!-- Batch Rows -->
                    <tr v-for="(batch, index) in data?.training?.events" :key="batch.id || index">
                      <td class="align-middle">{{ formatBatchLabel(batch) }}</td>
                      <td class="align-middle">{{ formatPenyelenggara(batch.penyelenggara) }}</td>
                      <td class="align-middle">
                        <span class="text-muted">{{ batch.status }}</span>
                      </td>
                      <td class="align-middle">
                        <span v-if="batch.score !== null && batch.score !== undefined">
                          {{ batch.score }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <!-- <td class="align-middle">
                        <div v-if="batch.score !== null && batch.score !== undefined">
                          <span
                            class="badge badge-pill px-2 py-1 me-1"
                            :class="batch.score ? 'bg-success text-white' : 'bg-secondary text-white'"
                          >
                            {{ batch.moodle.completed ? "Completed" : "In Progress" }}
                          </span>
                        </div>
                        <span v-else class="text-muted">-</span>
                      </td> -->
                      <td class="align-middle">
                        <div class="d-flex gap-2">
                          <b-button
                            v-if="batch.isAllowedSubmitParticipant && data.status === 'open'"
                            class="no-wrap btn-pill"
                            variant="warning"
                            size="sm"
                            @click="handleSubmit(batch)"
                          >
                            <span class="fe fe-edit"></span>
                            Submit Enrollment
                          </b-button>
                          <span v-else :class="getTrainingStatusClass(batch?.status)">
                            {{ modifyStatus(batch?.status) || "" }}
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  formatCurrency,
  formatLongDate,
  formatTime,
  modifyStatus,
  snakeToTitle,
  getTrainingStatusClass,
} from "../utils/utils";
import { formatBatchLabel, formatPenyelenggara } from "@/utils/batchIdentity";

export default {
  name: "StructuredLearningModal",

  components: {},

  props: {
    modalId: { type: String, default: "structured-learning-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      activeTab: "description",
    };
  },

  computed: {
    defaultActiveTab() {
      if (this.data?.certificate) return "certificate";
      if (this.data?.training?.platform === "Other") return "schedule";
      return "description";
    },
  },

  watch: {
    data: {
      immediate: true,
      handler() {
        this.resetActiveTab();
      },
    },
  },

  methods: {
    formatCurrency,
    snakeToTitle,
    getTrainingStatusClass,
    formatLongDate,
    formatTime,
    modifyStatus,
    formatBatchLabel,
    formatPenyelenggara,
    resetActiveTab() {
      this.activeTab = this.defaultActiveTab;
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    isActiveTab(tab) {
      return this.activeTab === tab;
    },
    handleSubmit(batch) {
      const payload = {
        id: this.data.id,
        training_event_id: batch.id,
      };

      this.$emit("submit-enrollment", payload);
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || "";

      if (type.includes("certification") && !type.includes("semi")) {
        return "fe fe-file-text";
      } else if (type.includes("semi")) {
        return "fe fe-shield";
      } else if (type.includes("training")) {
        return "fe fe-user";
      }

      return "fe fe-award";
    },

    handleImageError(event) {
      event.target.src = "https://dummyimage.com/300x200/FAFAFA/000000&text=Course";
    },

    handleCertImageError(event) {
      event.target.src = "https://dummyimage.com/300x200/FAFAFA/000000&text=Certificate";
    },

    isPdfCertificate(certificate) {
      if (typeof certificate !== "string") return false;

      const cleanUrl = certificate.split("?")[0]?.toLowerCase();

      return cleanUrl.endsWith(".pdf");
    },

    previewCertificate() {
      if (this.data?.certificate) {
        const certUrl = `${this.data?.certificate}`;
        window.open(certUrl, "_blank");
      }
    },

    formatType(type) {
      const typeMap = {
        internal: "Internal",
        external: "External",
        external_lms: "External LMS",
      };
      return typeMap[type] || type || "-";
    },
  },
};
</script>

<style scoped>
.w-50 {
  width: 50%;
}

/* Badge Styles */
.badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  font-size: 0.75rem;
}

.badge-custom {
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.35rem;
}

/* Course Image Landscape Container */
.course-image-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  background-color: #fafafa;
}

.course-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* PDF Overlay for course-image-container */
.pdf-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
}

/* Schedule Table Styles */
.table {
  margin-bottom: 0;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #f1f1f1;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Status Badge Styles */
.badge-warning {
  background-color: #fef5e4 !important;
  color: #f6c343 !important;
  font-weight: 500;
}

.badge-info {
  background-color: #e4f4f7 !important;
  color: #39afd1 !important;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda !important;
  color: #00d97e !important;
  font-weight: 500;
}

.badge-secondary {
  background-color: #e9ecef !important;
  color: #6c757d !important;
  font-weight: 500;
}

/* Certificate Tab Styles */
.certificate-section,
.info-section {
  height: 100%;
}

.certificate-preview {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #dee2e6;
}

.certificate-preview:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Certificate Full Image Styles */
.certificate-full-container {
  width: 100%;
  text-align: center;
}

.certificate-full-image {
  max-width: 100%;
  /* max-height: 500px; */
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.certificate-full-image:hover {
  transform: scale(1.01);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* PDF Full Thumbnail Styles */
.pdf-thumbnail-full {
  cursor: pointer;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail-full:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container-large {
  width: 100px;
  height: 130px;
  margin: 0 auto;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container-large i {
  font-size: 3.5rem;
  color: #fff;
}

.alert-heading {
  margin-bottom: 0.5rem;
}

/* PDF Thumbnail Styles */
.pdf-thumbnail {
  cursor: pointer;
  padding: 2rem;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container {
  width: 80px;
  height: 100px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-label {
  font-weight: 600;
  color: #1a202c;
  font-size: 1rem;
}
</style>
